// src/components/settings/sub_school_settings/GeneralSettings.js

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
    Button, TextField, Grid, Typography, Snackbar, Alert, Box, Link, Paper,
    IconButton, Avatar, Stack, CircularProgress, useTheme, alpha, Tooltip,
    MenuItem, Select, InputLabel, FormControl, Tabs, Tab, Fade // Added Tabs, Tab, Fade
} from '@mui/material';
import {
    Save as SaveIcon, Cancel as CancelIcon, PhotoCamera as PhotoCameraIcon,
    Business as BusinessIcon, ContactMail as ContactMailIcon, Link as LinkIcon,
    Description as DescriptionIcon, AccessTime as AccessTimeIcon, Category as CategoryIcon,
    AccountBalance as AccountBalanceIcon, VerifiedUser as VerifiedUserIcon,
    DriveFileRenameOutline as SignatureIcon, LocationOn as LocationOnIcon,
    GpsFixed as GpsFixedIcon, Info as InfoIcon, Language as LanguageIcon,
    CameraAlt as CameraAltIcon, // For Media Tab
    Edit as EditIcon, // Added Edit Icon for triggering dialog
} from '@mui/icons-material';
import axiosInstance from '../../../api/axiosConfig'; // Ensure axiosInstance has the uploadImage helper
import ImageUploadDialog from '../../common/ImageUploadDialog'; // Import the dialog
import { hasAllowedRole, splitRoles } from '../../../utils/authSession';
import {
    LETTER_HEADER_OPTIONS,
    LETTER_HEADER_STORAGE_KEY,
    REPORT_TEMPLATE_OPTIONS,
    REPORT_TEMPLATE_STORAGE_KEY,
} from '../../../utils/documentStyleOptions';

// --- Helper: Tab Panel Component ---
function TabPanel(props) {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`settings-tabpanel-${index}`}
            aria-labelledby={`settings-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ pt: 3 }}> {/* Add padding top to tab content */}
                    {children}
                </Box>
            )}
        </div>
    );
}

function a11yProps(index) {
    return {
        id: `settings-tab-${index}`,
        'aria-controls': `settings-tabpanel-${index}`,
    };
}

// --- Main Settings Component ---
const GeneralSettings = () => {
    const theme = useTheme();
    const [settings, setSettings] = useState(null); // Fetched state
    const [editedSettings, setEditedSettings] = useState(null); // Current form state
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
    const [currentTab, setCurrentTab] = useState(0);
    const [locating, setLocating] = useState(false);

    // Image Previews - Still needed for display
    const [logoPreview, setLogoPreview] = useState(null);
    const [principalSignaturePreview, setPrincipalSignaturePreview] = useState(null);
    const [directorSignaturePreview, setDirectorSignaturePreview] = useState(null);
    const [schoolSealPreview, setSchoolSealPreview] = useState(null);

    // Dialog State
    const [dialogOpen, setDialogOpen] = useState(false);
    const [currentImageType, setCurrentImageType] = useState(null); // 'logo', 'principalSignature', etc.

    const rawSchoolId = localStorage.getItem('schoolId');
    const schoolId =
        rawSchoolId && rawSchoolId !== 'null' && rawSchoolId !== 'undefined'
            ? rawSchoolId
            : null;
    const userRole = localStorage.getItem('role');
    const canEdit = hasAllowedRole(['SCHOOL_ADMIN', 'SUPER_ADMIN'], splitRoles(userRole));

    // Map internal types to backend API types
    const imageTypeMap = useMemo(() => ({
        logo: 'SCHOOL_LOGOS',
        principalSignature: 'PRINCIPAL_SIGNATURES',
        directorSignature: 'DIRECTOR_SIGNATURES',
        schoolSeal: 'SCHOOL_SEALS',
    }), []);

    // --- Snackbar Utility ---
    const showSnackbar = useCallback((message, severity = 'info') => {
        setSnackbar({ open: true, message, severity });
    }, []);

    const handleFetchCurrentLocation = useCallback(() => {
        if (!navigator?.geolocation) {
            showSnackbar('Geolocation is not supported by this browser.', 'error');
            return;
        }

        setLocating(true);
        navigator.geolocation.getCurrentPosition(
            (position) => {
                const { latitude, longitude } = position.coords;
                setEditedSettings((prev) => ({
                    ...(prev || {}),
                    latitude: Number(latitude.toFixed(6)),
                    longitude: Number(longitude.toFixed(6)),
                }));
                showSnackbar('Current location fetched. Please save changes.', 'success');
                setLocating(false);
            },
            (error) => {
                let message = 'Unable to fetch current location.';
                if (error?.code === 1) message = 'Location permission denied. Please allow location access.';
                if (error?.code === 2) message = 'Location unavailable. Please enable GPS/location services.';
                if (error?.code === 3) message = 'Location request timed out. Please try again.';
                showSnackbar(message, 'error');
                setLocating(false);
            },
            { enableHighAccuracy: true, timeout: 15000, maximumAge: 10000 }
        );
    }, [showSnackbar]);

    // --- Data Fetching ---
    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            if (!schoolId) {
                showSnackbar('School ID is missing. Please re-login and open School Settings again.', 'warning');
                setSettings({});
                setEditedSettings({});
                return;
            }

            const applyFetchedData = (fetchedData = {}) => {
                setSettings(fetchedData);
                setEditedSettings(fetchedData);
                setLogoPreview(fetchedData.logoUrl || null);
                setPrincipalSignaturePreview(fetchedData.principalSignatureUrl || null);
                setDirectorSignaturePreview(fetchedData.directorSignatureUrl || null);
                setSchoolSealPreview(fetchedData.schoolSealUrl || null);
            };

            const mergeSchoolAndConfig = (schoolData = {}, configData = {}) => ({
                ...schoolData,
                name: schoolData.name || configData.school_name || '',
                schoolCode: schoolData.schoolCode || configData.school_code || '',
                email: schoolData.email || configData.school_email || '',
                phone: schoolData.phone || configData.school_phone || '',
                address: schoolData.address || configData.school_address || '',
                description: schoolData.description || configData.school_description || '',
                principalName: schoolData.principalName || configData.principal_name || '',
                websiteUrl: schoolData.websiteUrl || configData.school_website_url || '',
                logoUrl: schoolData.logoUrl || configData.school_logo_url || '',
                slogan: schoolData.slogan || configData.school_slogan || '',
                academicYear: schoolData.academicYear || configData.academic_year || '',
                reportPdfTemplateDefault: configData.report_pdf_template_default || REPORT_TEMPLATE_OPTIONS[0].value,
                letterHeaderTemplateDefault: configData.letter_header_template_default || LETTER_HEADER_OPTIONS[0].value,
                employeeLoginAuthMode: configData.employee_login_auth_mode || 'PASSWORD',
                studentLoginAuthMode: configData.student_login_auth_mode || 'BOTH',
                employeeMobileLoginEnabled: configData.employee_mobile_login_enabled || 'true',
                studentMobileLoginEnabled: configData.student_mobile_login_enabled || 'true',
                parentMobileLoginEnabled: configData.parent_mobile_login_enabled || 'true',
                openingHours: schoolData.openingHours || configData.opening_hours || '',
                recognisedBy: schoolData.recognisedBy || configData.recognised_by || '',
                affiliationBoard: schoolData.affiliationBoard || configData.affiliation_board || '',
                subDomain: schoolData.subDomain || configData.sub_domain || '',
                customDomain: schoolData.customDomain || configData.custom_domain || '',
                latitude: schoolData.latitude ?? configData.latitude ?? '',
                longitude: schoolData.longitude ?? configData.longitude ?? '',
                punchInRadiusMeters: configData.punch_in_radius_meters ?? '200',
            });

            try {
                const [schoolResponse, configResponse] = await Promise.all([
                    axiosInstance.get(`/api/schools/${schoolId}`),
                    axiosInstance.get(`/api/settings/school/${schoolId}`),
                ]);
                applyFetchedData(mergeSchoolAndConfig(schoolResponse.data || {}, configResponse.data || {}));
            } catch (error) {
                if (error?.response?.status !== 404) {
                    throw error;
                }

                const [fallbackResponse, configResponse] = await Promise.all([
                    axiosInstance.get('/api/schools', {
                        params: { page: 0, size: 500 },
                    }),
                    axiosInstance.get(`/api/settings/school/${schoolId}`),
                ]);
                const schoolList = Array.isArray(fallbackResponse.data?.content)
                    ? fallbackResponse.data.content
                    : [];
                const fallbackSchool = schoolList.find(
                    (item) => String(item.schoolId) === String(schoolId)
                );

                if (!fallbackSchool) {
                    throw error;
                }

                applyFetchedData(mergeSchoolAndConfig(fallbackSchool, configResponse.data || {}));
            }
        } catch (error) {
            console.error('Error fetching school details:', error);
            showSnackbar('Error fetching school details.', 'error');
            setSettings({});
            setEditedSettings({});
        } finally {
            setLoading(false);
        }
    }, [schoolId, showSnackbar]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // --- Change Detection ---
    const hasChanges = useMemo(() => {
        if (!settings || !editedSettings) return false;
        return JSON.stringify(settings) !== JSON.stringify(editedSettings);
    }, [settings, editedSettings]);

    // --- Event Handlers ---
    const handleChange = useCallback((e) => {
        const { name, value } = e.target;
        setEditedSettings((prev) => ({ ...prev, [name]: value }));
    }, []);

    const handleTabChange = (event, newValue) => {
        setCurrentTab(newValue);
    };

    // --- Dialog Handlers ---
    const handleOpenUploadDialog = useCallback((imageType) => {
        if (!canEdit) return;
        setCurrentImageType(imageType);
        setDialogOpen(true);
    }, [canEdit]);

    const handleCloseUploadDialog = useCallback(() => {
        setDialogOpen(false);
        // Optionally reset currentImageType, though it might be useful to keep it for debugging
        // setCurrentImageType(null);
    }, []);

    const handleUploadComplete = useCallback((uploadedUrl) => {
        if (!currentImageType) return; // Should not happen

        const urlField = {
            logo: 'logoUrl', principalSignature: 'principalSignatureUrl',
            directorSignature: 'directorSignatureUrl', schoolSeal: 'schoolSealUrl',
        }[currentImageType];

        const previewSetter = {
            logo: setLogoPreview, principalSignature: setPrincipalSignaturePreview,
            directorSignature: setDirectorSignaturePreview, schoolSeal: setSchoolSealPreview,
        }[currentImageType];

        // Update the form state (editedSettings)
        setEditedSettings(prev => ({ ...prev, [urlField]: uploadedUrl }));
        // Update the preview state
        previewSetter(uploadedUrl);

        // No need to show snackbar here, dialog does it.
        // showSnackbar(`${labelFromType(currentImageType)} updated successfully. Save changes to finalize.`, 'success');

    }, [currentImageType]); // Add dependencies

    // --- Save/Cancel Handlers ---
    const handleSave = useCallback(async () => {
        setSaving(true);
        try {
            await axiosInstance.put(`/api/schools/${schoolId}`, editedSettings);
            await axiosInstance.post('/api/settings/school', {
                schoolId: Number(schoolId),
                settingKey: 'punch_in_radius_meters',
                settingValue: editedSettings.punchInRadiusMeters ? String(editedSettings.punchInRadiusMeters) : '200',
                description: 'Allowed radius in meters for employee punch in and punch out',
            });
            await axiosInstance.post('/api/settings/school', {
                schoolId: Number(schoolId),
                settingKey: 'report_pdf_template_default',
                settingValue: editedSettings.reportPdfTemplateDefault || REPORT_TEMPLATE_OPTIONS[0].value,
                description: 'Default report PDF template selected by the school admin',
            });
            await axiosInstance.post('/api/settings/school', {
                schoolId: Number(schoolId),
                settingKey: 'letter_header_template_default',
                settingValue: editedSettings.letterHeaderTemplateDefault || LETTER_HEADER_OPTIONS[0].value,
                description: 'Default letter header template selected by the school admin',
            });
            await axiosInstance.post('/api/settings/school', {
                schoolId: Number(schoolId),
                settingKey: 'employee_login_auth_mode',
                settingValue: editedSettings.employeeLoginAuthMode || 'PASSWORD',
                description: 'Employee login mode: PASSWORD, OTP, or BOTH',
            });
            await axiosInstance.post('/api/settings/school', {
                schoolId: Number(schoolId),
                settingKey: 'student_login_auth_mode',
                settingValue: editedSettings.studentLoginAuthMode || 'BOTH',
                description: 'Student login mode: PASSWORD, OTP, or BOTH',
            });
            await axiosInstance.post('/api/settings/school', {
                schoolId: Number(schoolId),
                settingKey: 'employee_mobile_login_enabled',
                settingValue: String(editedSettings.employeeMobileLoginEnabled ?? 'true'),
                description: 'Allow employees to login with mobile number',
            });
            await axiosInstance.post('/api/settings/school', {
                schoolId: Number(schoolId),
                settingKey: 'student_mobile_login_enabled',
                settingValue: String(editedSettings.studentMobileLoginEnabled ?? 'true'),
                description: 'Allow students to login with mobile number',
            });
            await axiosInstance.post('/api/settings/school', {
                schoolId: Number(schoolId),
                settingKey: 'parent_mobile_login_enabled',
                settingValue: String(editedSettings.parentMobileLoginEnabled ?? 'true'),
                description: 'Allow parents to login with registered parent mobile number',
            });
            setSettings(editedSettings); // Update base state on success
            showSnackbar('Changes saved successfully.', 'success');
            localStorage.setItem('schoolName', editedSettings.name || '');
            localStorage.setItem('slogan', editedSettings.slogan || '');
            localStorage.setItem('logoUrl', editedSettings.logoUrl || '');
            localStorage.setItem(`${REPORT_TEMPLATE_STORAGE_KEY}_${schoolId}`, editedSettings.reportPdfTemplateDefault || REPORT_TEMPLATE_OPTIONS[0].value);
            localStorage.setItem(`${LETTER_HEADER_STORAGE_KEY}_${schoolId}`, editedSettings.letterHeaderTemplateDefault || LETTER_HEADER_OPTIONS[0].value);
            window.dispatchEvent(new Event('storage'));
        } catch (error) {
            console.error('Error saving school details:', error);
            showSnackbar(error.response?.data?.message || 'Error saving school details.', 'error');
        } finally {
            setSaving(false);
        }
    }, [editedSettings, schoolId, showSnackbar]);

    const handleCancel = useCallback(() => {
        setEditedSettings(settings);
        setLogoPreview(settings?.logoUrl || null);
        setPrincipalSignaturePreview(settings?.principalSignatureUrl || null);
        setDirectorSignaturePreview(settings?.directorSignatureUrl || null);
        setSchoolSealPreview(settings?.schoolSealUrl || null);
        showSnackbar('Changes cancelled.', 'warning');
    }, [settings, showSnackbar]);

    const handleCloseSnackbar = (event, reason) => {
        if (reason === 'clickaway') return;
        setSnackbar({ ...snackbar, open: false });
    };

    // --- Helpers ---
    const labelFromType = useCallback((type) => {
        switch (type) {
            case 'logo': return 'Logo';
            case 'principalSignature': return 'Principal Signature';
            case 'directorSignature': return 'Director Signature';
            case 'schoolSeal': return 'School Seal';
            default: return 'Image';
        }
    }, []);

    const ReadOnlyField = ({ label, value, link }) => (
        <Box mb={1.5}>
            <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
            {link && value ? (
                <Link href={link} underline="hover" variant="body2">{value}</Link>
            ) : (
                <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{value || 'N/A'}</Typography>
            )}
        </Box>
    );

    // Simplified Busy State
    const isBusy = saving;

    // --- Render ---
    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}><CircularProgress /></Box>;
    }
    if (!settings || !editedSettings) {
        return <Alert severity="error" sx={{ m: 2 }}>Could not load school settings.</Alert>;
    }

    return (
        <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, position: 'relative' }}>
            {/* --- Header --- */}
            <Stack direction={{ xs: 'column', sm: 'row' }} alignItems="center" spacing={2} mb={3}>
                 <Box sx={{ position: 'relative' }}>
                     <Avatar
                         src={logoPreview || undefined}
                         alt="School Logo"
                         sx={{ width: 64, height: 64, bgcolor: alpha(theme.palette.primary.light, 0.3) }}
                     >
                         {!logoPreview && <BusinessIcon sx={{ fontSize: 32, color: theme.palette.primary.main }} />}
                     </Avatar>
                     {canEdit && (
                         <IconButton
                             size="small"
                             onClick={() => handleOpenUploadDialog('logo')}
                             sx={{
                                 position: 'absolute',
                                 bottom: -5,
                                 right: -5,
                                 bgcolor: 'background.paper',
                                 border: `1px solid ${theme.palette.divider}`,
                                 '&:hover': { bgcolor: alpha(theme.palette.primary.light, 0.1) }
                             }}
                             disabled={isBusy}
                             aria-label="Edit Logo"
                         >
                             <EditIcon sx={{ fontSize: '1rem' }} />
                         </IconButton>
                     )}
                 </Box>
                <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
                        {editedSettings?.name || settings?.name || 'School Settings'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {editedSettings?.slogan || settings?.slogan || 'Manage general school information'}
                    </Typography>
                </Box>
            </Stack>

            {/* --- Tabs Navigation --- */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 0 }}>
                <Tabs value={currentTab} onChange={handleTabChange} aria-label="settings tabs" variant="scrollable" scrollButtons="auto">
                    <Tab label="General" icon={<InfoIcon />} iconPosition="start" {...a11yProps(0)} />
                    <Tab label="Contact" icon={<ContactMailIcon />} iconPosition="start" {...a11yProps(1)} />
                    <Tab label="Online" icon={<LanguageIcon />} iconPosition="start" {...a11yProps(2)} />
                    <Tab label="Affiliation" icon={<AccountBalanceIcon />} iconPosition="start" {...a11yProps(3)} />
                    <Tab label="Location" icon={<LocationOnIcon />} iconPosition="start" {...a11yProps(4)} />
                    <Tab label="Media" icon={<CameraAltIcon />} iconPosition="start" {...a11yProps(5)} />
                </Tabs>
            </Box>

            {/* --- Tab Content --- */}
            <TabPanel value={currentTab} index={0}>
                 <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 500, mb: 2.5 }}>General Information</Typography>
                    <Grid container spacing={2.5}>
                         <Grid item xs={12} md={6}>
                            <TextField fullWidth label="School Name" name="name" value={editedSettings.name || ''} onChange={handleChange} disabled={!canEdit || isBusy} size="small" required InputLabelProps={{ shrink: true }}/>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField fullWidth label="Slogan / Motto" name="slogan" value={editedSettings.slogan || ''} onChange={handleChange} disabled={!canEdit || isBusy} size="small" InputLabelProps={{ shrink: true }}/>
                        </Grid>
                         <Grid item xs={12} md={6}>
                            <TextField fullWidth label="Established Year" name="establishedYear" type="number" value={editedSettings.establishedYear || ''} onChange={handleChange} disabled={!canEdit || isBusy} size="small" InputLabelProps={{ shrink: true }}/>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField fullWidth label="Principal Name" name="principalName" value={editedSettings.principalName || ''} onChange={handleChange} disabled={!canEdit || isBusy} size="small" InputLabelProps={{ shrink: true }}/>
                        </Grid>
                         <Grid item xs={12} md={6}>
                             <TextField fullWidth label="Opening Hours" name="openingHours" value={editedSettings.openingHours || ''} onChange={handleChange} disabled={!canEdit || isBusy} size="small" InputLabelProps={{ shrink: true }} placeholder="e.g., Mon-Fri 9 AM - 5 PM" />
                         </Grid>
                         <Grid item xs={12} md={6}>
                            <FormControl fullWidth size="small" disabled={!canEdit || isBusy}>
                                <InputLabel id="report-template-label">Default Report Template</InputLabel>
                                <Select
                                    labelId="report-template-label"
                                    name="reportPdfTemplateDefault"
                                    value={editedSettings.reportPdfTemplateDefault || REPORT_TEMPLATE_OPTIONS[0].value}
                                    onChange={handleChange}
                                    label="Default Report Template"
                                >
                                    {REPORT_TEMPLATE_OPTIONS.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                         </Grid>
                         <Grid item xs={12} md={6}>
                            <FormControl fullWidth size="small" disabled={!canEdit || isBusy}>
                                <InputLabel id="letter-header-template-label">Default Letter Header</InputLabel>
                                <Select
                                    labelId="letter-header-template-label"
                                    name="letterHeaderTemplateDefault"
                                    value={editedSettings.letterHeaderTemplateDefault || LETTER_HEADER_OPTIONS[0].value}
                                    onChange={handleChange}
                                    label="Default Letter Header"
                                >
                                    {LETTER_HEADER_OPTIONS.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                         </Grid>
                         <Grid item xs={12}>
                             <Paper
                                 variant="outlined"
                                 sx={{
                                     p: 2,
                                     borderRadius: 2,
                                     background: alpha(theme.palette.primary.main, 0.03),
                                     borderColor: alpha(theme.palette.primary.main, 0.15),
                                 }}
                             >
                                 <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>
                                     Document Style Defaults
                                 </Typography>
                                 <Typography variant="body2" color="text.secondary">
                                     School admins can control the default report style and the default letter header from here. These defaults are used across reports and generated documents.
                                 </Typography>
                                 <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
                                     Report style: {REPORT_TEMPLATE_OPTIONS.find((option) => option.value === (editedSettings.reportPdfTemplateDefault || REPORT_TEMPLATE_OPTIONS[0].value))?.description}
                                 </Typography>
                                 <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
                                     Letter header: {LETTER_HEADER_OPTIONS.find((option) => option.value === (editedSettings.letterHeaderTemplateDefault || LETTER_HEADER_OPTIONS[0].value))?.description}
                                 </Typography>
                             </Paper>
                         </Grid>
                         <Grid item xs={12}>
                             <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                 <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
                                     Login Access
                                 </Typography>
                                 <Grid container spacing={2}>
                                     <Grid item xs={12} md={4}>
                                         <FormControl fullWidth size="small" disabled={!canEdit || isBusy}>
                                             <InputLabel id="employee-login-mode-label">Employee Login Mode</InputLabel>
                                             <Select
                                                 labelId="employee-login-mode-label"
                                                 name="employeeLoginAuthMode"
                                                 value={editedSettings.employeeLoginAuthMode || 'PASSWORD'}
                                                 onChange={handleChange}
                                                 label="Employee Login Mode"
                                             >
                                                 <MenuItem value="PASSWORD">Password Only</MenuItem>
                                                 <MenuItem value="OTP">OTP Only</MenuItem>
                                                 <MenuItem value="BOTH">Password + OTP</MenuItem>
                                             </Select>
                                         </FormControl>
                                     </Grid>
                                     <Grid item xs={12} md={4}>
                                         <FormControl fullWidth size="small" disabled={!canEdit || isBusy}>
                                             <InputLabel id="student-login-mode-label">Student Login Mode</InputLabel>
                                             <Select
                                                 labelId="student-login-mode-label"
                                                 name="studentLoginAuthMode"
                                                 value={editedSettings.studentLoginAuthMode || 'BOTH'}
                                                 onChange={handleChange}
                                                 label="Student Login Mode"
                                             >
                                                 <MenuItem value="PASSWORD">Password Only</MenuItem>
                                                 <MenuItem value="OTP">OTP Only</MenuItem>
                                                 <MenuItem value="BOTH">Password + OTP</MenuItem>
                                             </Select>
                                         </FormControl>
                                     </Grid>
                                     <Grid item xs={12} md={4}>
                                         <FormControl fullWidth size="small" disabled={!canEdit || isBusy}>
                                             <InputLabel id="employee-mobile-login-enabled-label">Employee Mobile Login</InputLabel>
                                             <Select
                                                 labelId="employee-mobile-login-enabled-label"
                                                 name="employeeMobileLoginEnabled"
                                                 value={String(editedSettings.employeeMobileLoginEnabled ?? 'true')}
                                                 onChange={handleChange}
                                                 label="Employee Mobile Login"
                                             >
                                                 <MenuItem value="true">Enabled</MenuItem>
                                                 <MenuItem value="false">Disabled</MenuItem>
                                             </Select>
                                         </FormControl>
                                     </Grid>
                                     <Grid item xs={12} md={6}>
                                         <FormControl fullWidth size="small" disabled={!canEdit || isBusy}>
                                             <InputLabel id="student-mobile-login-enabled-label">Student Mobile Login</InputLabel>
                                             <Select
                                                 labelId="student-mobile-login-enabled-label"
                                                 name="studentMobileLoginEnabled"
                                                 value={String(editedSettings.studentMobileLoginEnabled ?? 'true')}
                                                 onChange={handleChange}
                                                 label="Student Mobile Login"
                                             >
                                                 <MenuItem value="true">Enabled</MenuItem>
                                                 <MenuItem value="false">Disabled</MenuItem>
                                             </Select>
                                         </FormControl>
                                     </Grid>
                                     <Grid item xs={12} md={6}>
                                         <FormControl fullWidth size="small" disabled={!canEdit || isBusy}>
                                             <InputLabel id="parent-mobile-login-enabled-label">Parent Mobile Login</InputLabel>
                                             <Select
                                                 labelId="parent-mobile-login-enabled-label"
                                                 name="parentMobileLoginEnabled"
                                                 value={String(editedSettings.parentMobileLoginEnabled ?? 'true')}
                                                 onChange={handleChange}
                                                 label="Parent Mobile Login"
                                             >
                                                 <MenuItem value="true">Enabled</MenuItem>
                                                 <MenuItem value="false">Disabled</MenuItem>
                                             </Select>
                                         </FormControl>
                                     </Grid>
                                 </Grid>
                             </Paper>
                         </Grid>
                         <Grid item xs={12}>
                             <TextField fullWidth label="Description" name="description" value={editedSettings.description || ''} onChange={handleChange} disabled={!canEdit || isBusy} size="small" multiline rows={4} InputLabelProps={{ shrink: true }} placeholder="A brief description about the school..." />
                         </Grid>
                    </Grid>
                </Paper>
            </TabPanel>

            <TabPanel value={currentTab} index={1}>
                 <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 500, mb: 2.5 }}>Contact Details</Typography>
                    <Grid container spacing={3}> {/* Increased spacing slightly */}
                         <Grid item xs={12} md={5}> {/* Adjusted grid size */}
                             <Typography variant="subtitle2" gutterBottom>Static Information</Typography>
                             <ReadOnlyField label="School Code" value={settings.schoolCode}/>
                             <ReadOnlyField label="Registered Email" value={settings.email} />
                             <ReadOnlyField label="Registered Phone" value={settings.phone} />
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                    To change School Code, Email, or Phone, please <Link href="mailto:support@skhool.in" underline="hover">contact support</Link>.
                                </Typography>
                         </Grid>
                         <Grid item xs={12} md={7}> {/* Adjusted grid size */}
                             <Typography variant="subtitle2" gutterBottom>Editable Contact Info</Typography>
                            <TextField fullWidth label="Contact Person Name" name="contactPersonName" value={editedSettings.contactPersonName || ''} onChange={handleChange} disabled={!canEdit || isBusy} size="small" InputLabelProps={{ shrink: true }} sx={{mb: 2.5}}/>
                            <TextField fullWidth label="Contact Person Phone" name="contactPersonPhoneNumber" value={editedSettings.contactPersonPhoneNumber || ''} onChange={handleChange} disabled={!canEdit || isBusy} size="small" InputLabelProps={{ shrink: true }} sx={{mb: 2.5}}/>
                             <TextField fullWidth label="School Address" name="address" value={editedSettings.address || ''} onChange={handleChange} disabled={!canEdit || isBusy} size="small" multiline rows={3} InputLabelProps={{ shrink: true }}/>
                         </Grid>
                    </Grid>
                 </Paper>
            </TabPanel>

            {/* Add other TabPanels similarly... */}
             <TabPanel value={currentTab} index={2}>
                 <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 500, mb: 2.5 }}>Online Presence</Typography>
                     <Grid container spacing={2.5}>
                          <Grid item xs={12} md={6}>
                              <TextField fullWidth label="Website URL" name="websiteUrl" value={editedSettings.websiteUrl || ''} onChange={handleChange} disabled={!canEdit || isBusy} size="small" InputLabelProps={{ shrink: true }} placeholder="https://www.yourschool.com"/>
                          </Grid>
                         <Grid item xs={12} md={6}>
                             <TextField fullWidth label="Facebook Link" name="facebookLink" value={editedSettings.facebookLink || ''} onChange={handleChange} disabled={!canEdit || isBusy} size="small" InputLabelProps={{ shrink: true }} placeholder="https://facebook.com/yourschool"/>
                         </Grid>
                         <Grid item xs={12} md={6}>
                             <TextField fullWidth label="Twitter (X) Link" name="twitterLink" value={editedSettings.twitterLink || ''} onChange={handleChange} disabled={!canEdit || isBusy} size="small" InputLabelProps={{ shrink: true }} placeholder="https://x.com/yourschool"/>
                         </Grid>
                         <Grid item xs={12} md={6}>
                             <TextField fullWidth label="Instagram Link" name="instagramLink" value={editedSettings.instagramLink || ''} onChange={handleChange} disabled={!canEdit || isBusy} size="small" InputLabelProps={{ shrink: true }} placeholder="https://instagram.com/yourschool"/>
                         </Grid>
                     </Grid>
                 </Paper>
            </TabPanel>

             <TabPanel value={currentTab} index={3}>
                <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 500, mb: 2.5 }}>Affiliation & Type</Typography>
                    <Grid container spacing={2.5}>
                      <Grid item xs={12} md={4}>
                        <FormControl fullWidth size="small" disabled={!canEdit || isBusy}>
                            <InputLabel id="school-type-label">School Type</InputLabel>
                            <Select
                            labelId="school-type-label"
                            name="schoolType"
                            value={editedSettings.schoolType || ''}
                            onChange={handleChange}
                            label="School Type"
                            >
                            <MenuItem value=""><em>None</em></MenuItem>
                            <MenuItem value="PUBLIC">Public</MenuItem>
                            <MenuItem value="PRIVATE">Private</MenuItem>
                            <MenuItem value="BOARDING_SCHOOL">Boarding School</MenuItem>
                            <MenuItem value="INTERNATIONAL">International</MenuItem>
                            <MenuItem value="GOVERNMENT">Government</MenuItem>
                            <MenuItem value="CHRISTIAN_MISSIONARY">Christian Missionary</MenuItem>
                            <MenuItem value="OTHERS">Others</MenuItem> 
                            </Select>
                        </FormControl>
                        </Grid>

                          <Grid item xs={12} md={4}>
                            <FormControl fullWidth size="small" disabled={!canEdit || isBusy}>
                                <InputLabel id="affiliation-board-label">Affiliation Board</InputLabel>
                                <Select
                                labelId="affiliation-board-label"
                                id="affiliation-board"
                                name="affiliationBoard"
                                value={editedSettings.affiliationBoard || ''}
                                onChange={handleChange}
                                label="Affiliation Board"
                                >
                                <MenuItem value=""><em>None</em></MenuItem>
                                <MenuItem value="CBSE">CBSE</MenuItem>
                                <MenuItem value="ICSE">ICSE</MenuItem>
                                <MenuItem value="STATE_BOARD">State Board</MenuItem>
                                <MenuItem value="IB">International Baccalaureate (IB)</MenuItem>
                                <MenuItem value="IGCSE">IGCSE</MenuItem>
                                <MenuItem value="OTHER">Other</MenuItem>
                                </Select>
                            </FormControl>
                            </Grid>

                         <Grid item xs={12} md={4}>
                             <TextField fullWidth label="Recognised By" name="recognisedBy" value={editedSettings.recognisedBy || ''} onChange={handleChange} disabled={!canEdit || isBusy} size="small" InputLabelProps={{ shrink: true }} placeholder="e.g., Dept. of Education" />
                         </Grid>
                     </Grid>
                </Paper>
            </TabPanel>

            <TabPanel value={currentTab} index={4}>
                 <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
                     <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
                         <Typography variant="h6" sx={{ fontWeight: 500 }}>Location Coordinates</Typography>
                         <Tooltip title="Use your current device location">
                             <span>
                                 <Button
                                     variant="outlined"
                                     size="small"
                                     startIcon={<GpsFixedIcon />}
                                     onClick={handleFetchCurrentLocation}
                                     disabled={!canEdit || isBusy || locating}
                                 >
                                     {locating ? 'Fetching...' : 'Use Current Location'}
                                 </Button>
                             </span>
                         </Tooltip>
                     </Box>
                    <Grid container spacing={2.5}>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth label="Latitude" name="latitude" type="number" value={editedSettings.latitude || ''} onChange={handleChange} disabled={!canEdit || isBusy} size="small" InputLabelProps={{ shrink: true }} inputProps={{ step: "any" }} placeholder="e.g., 12.9716" InputProps={{ startAdornment: <GpsFixedIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} /> }}/>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth label="Longitude" name="longitude" type="number" value={editedSettings.longitude || ''} onChange={handleChange} disabled={!canEdit || isBusy} size="small" InputLabelProps={{ shrink: true }} inputProps={{ step: "any" }} placeholder="e.g., 77.5946" InputProps={{ startAdornment: <GpsFixedIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} /> }}/>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Punch Radius (meters)"
                                name="punchInRadiusMeters"
                                type="number"
                                value={editedSettings.punchInRadiusMeters || '200'}
                                onChange={handleChange}
                                disabled={!canEdit || isBusy}
                                size="small"
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ min: 1, step: 1 }}
                                placeholder="e.g., 200"
                            />
                        </Grid>
                    </Grid>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>Used for map features and employee attendance geofence. Get precise coordinates using online tools.</Typography>
                 </Paper>
            </TabPanel>

            <TabPanel value={currentTab} index={5}>
                <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 500, mb: 2.5 }}>Media Assets</Typography>
                    <Grid container spacing={3} justifyContent="space-around"> {/* Use space-around or space-evenly */}
                        {/* Principal Signature */}
                        <Grid item xs={12} sm={6} md={4} sx={{ display: 'flex', justifyContent: 'center' }}>
                            <Stack spacing={1} alignItems="center">
                                <Typography variant="subtitle2" color="textSecondary">Principal Signature</Typography>
                                <Avatar src={principalSignaturePreview || undefined} alt="Principal Signature" variant="rounded" sx={{ width: 120, height: 120, bgcolor: 'action.hover' }}>{!principalSignaturePreview && <SignatureIcon/>}</Avatar>
                                {canEdit && <Button size="small" startIcon={<EditIcon />} onClick={() => handleOpenUploadDialog('principalSignature')} disabled={isBusy}>Edit</Button>}
                            </Stack>
                        </Grid>
                        {/* Director Signature */}
                         <Grid item xs={12} sm={6} md={4} sx={{ display: 'flex', justifyContent: 'center' }}>
                             <Stack spacing={1} alignItems="center">
                                <Typography variant="subtitle2" color="textSecondary">Director Signature</Typography>
                                <Avatar src={directorSignaturePreview || undefined} alt="Director Signature" variant="rounded" sx={{ width: 120, height: 120, bgcolor: 'action.hover' }}>{!directorSignaturePreview && <SignatureIcon/>}</Avatar>
                                {canEdit && <Button size="small" startIcon={<EditIcon />} onClick={() => handleOpenUploadDialog('directorSignature')} disabled={isBusy}>Edit</Button>}
                            </Stack>
                        </Grid>
                         {/* School Seal */}
                         <Grid item xs={12} sm={6} md={4} sx={{ display: 'flex', justifyContent: 'center' }}>
                             <Stack spacing={1} alignItems="center">
                                <Typography variant="subtitle2" color="textSecondary">School Seal</Typography>
                                <Avatar src={schoolSealPreview || undefined} alt="School Seal" variant="rounded" sx={{ width: 120, height: 120, bgcolor: 'action.hover' }}>{!schoolSealPreview && <VerifiedUserIcon/>}</Avatar>
                                {canEdit && <Button size="small" startIcon={<EditIcon />} onClick={() => handleOpenUploadDialog('schoolSeal')} disabled={isBusy}>Edit</Button>}
                            </Stack>
                        </Grid>
                    </Grid>
                 </Paper>
            </TabPanel>


            {/* --- Conditional Action Bar --- */}
            <Fade in={hasChanges && canEdit}>
                 <Paper
                     elevation={3}
                     sx={{
                         position: 'sticky', bottom: 0, left: 0, right: 0,
                         p: 1.5, bgcolor: alpha(theme.palette.background.paper, 0.95),
                         backdropFilter: 'blur(4px)', zIndex: theme.zIndex.appBar -1,
                         borderTop: `1px solid ${theme.palette.divider}`,
                         display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3,
                     }}
                 >
                     <Button variant="outlined" color="secondary" onClick={handleCancel} disabled={isBusy} size="small">
                         Cancel
                     </Button>
                     <Button variant="contained" color="primary" onClick={handleSave} disabled={isBusy} size="small" startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}>
                         Save Changes
                     </Button>
                 </Paper>
             </Fade>

            {/* --- Upload Dialog --- */}
            {canEdit && (
                <ImageUploadDialog
                    open={dialogOpen}
                    onClose={handleCloseUploadDialog}
                    onUploadComplete={handleUploadComplete}
                    showSnackbar={showSnackbar} // Pass down the snackbar utility
                    additionalFormData={{
                        assetType: "IMAGES"
                    }}
                    dialogTitle={`Upload ${labelFromType(currentImageType)}`}
                    descriptionLabel={`${labelFromType(currentImageType)} Label / Description`}
                />
            )}

            {/* --- Snackbar Notifications --- */}
            <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default GeneralSettings;
